import { useQuery } from "@apollo/client";
import Errors from "components/base/Errors";
import { Select } from "components/base/Form";
import { InlineSpinner } from "components/base/Spinner";
import useAutoScrollToBottom from "hooks/useAutoScrollToBottom";
import { OdooContext } from "OdooProvider";
import {
  forwardRef,
  Fragment,
  useContext,
  useImperativeHandle,
  useState,
} from "react";
import XLSX from "xlsx";
import { FETCH_CHINA_PRODUCT_STOCKS } from "./graphql";

const ODOO_COMPANY_NAMES = {
  0: "SS",
  1: "AB",
  3: "LTD",
};

const ChinaStocksProductTableView = forwardRef(({ products }, ref) => {
  const { odooUrl } = useContext(OdooContext);
  const [filter, setFilter] = useState("RECENT");
  const [companyFilter, setCompanyFilter] = useState("ALL");

  const productOdooIds = products.map((product) => product.odooId);
  const { loading, error, data } = useQuery(FETCH_CHINA_PRODUCT_STOCKS, {
    variables: { productOdooIds },
    fetchPolicy:
      process.env.NODE_ENV === "development" ? "cache-first" : "network-only",
  });
  const containerRef = useAutoScrollToBottom(data);

  useImperativeHandle(ref, () => ({
    download() {
      console.log("chinaStocks", chinaStocks);

      if (!data) return;

      const excelData = chinaStocks.map((i) => {
        const row = {
          Date: i.date,
          Reference: `[${i.type}] ${i.origin}`,
        };
        products.forEach((product) => {
          const keys = product.useSupplierStock
            ? ["QTY", "ODOO AB", "ODOO LTD", "SS", "FC"]
            : ["QTY", "ODOO AB", "ODOO LTD"];
          keys.forEach((key) => {
            row[`${product.number} ${key}`] = i[`${product.odooId} ${key}`];
          });
        });
        return row;
      });
      const sheet = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, sheet);
      const filename = `ChinaStocks-${new Date().toISOString()}.xlsx`;
      XLSX.writeFile(wb, filename);
    },
  }));

  if (loading)
    return (
      <div className="relative p-20">
        <InlineSpinner />
      </div>
    );
  if (error) return <Errors error={error} />;

  const chinaStocks = JSON.parse(data.chinaStocks);

  console.log("chinaStocks", chinaStocks);

  let res = filter === "RECENT" ? chinaStocks.slice(-200) : chinaStocks;
  if (companyFilter !== "ALL") {
    res = res.filter((stock) => {
      if (companyFilter === "AB")
        return stock.company_id === 1 || stock.company_id === 0;
      if (companyFilter === "LTD")
        return stock.company_id === 3 || stock.company_id === 0;
      return false;
    });
  }

  const lastRow = res.length > 0 ? res[res.length - 1] : null;

  const rowBgColor = (type) => {
    if (type === "PO") return "bg-green-50 dark:bg-green-900";
    if (type === "PLAN") return "bg-sky-50 dark:bg-sky-900";
    if (type === "SS") return "bg-orange-50 dark:bg-orange-900";
    if (type === "OTHER") return "bg-purple-50 dark:bg-purple-900";
    return "";
  };

  function cellStyle(stock, product) {
    if (stock[`${product.odooId} QTY`] === 0) return "";
    return rowBgColor(stock.type);
  }

  const getKeys = (product) =>
    (product.useSupplierStock
      ? ["QTY", "ODOO AB", "ODOO LTD", "SS", "FC"]
      : ["QTY", "ODOO AB", "ODOO LTD"]
    ).filter((key) => {
      if (companyFilter === "ALL") return true;
      if (companyFilter === "AB") return key !== "ODOO LTD";
      if (companyFilter === "LTD") return key !== "ODOO AB";
      return false;
    });

  return (
    <div className="overflow-auto" ref={containerRef}>
      <table className="whitespace-nowrap bg-white dark:bg-gray-800">
        <thead>
          <tr className="sticky bg-gray-100 dark:bg-gray-700 top-0 z-20">
            <th
              className="border-r  dark:border-gray-700 px-4 bg-gray-200 dark:bg-gray-700 z-10 sticky left-0"
              rowSpan={2}
            >
              <div className="flex items-center space-x-2">
                <div>Date & Reference</div>
                <Select
                  className="bg-transparent dark:bg-transparent p-0 font-semibold text-blue-600 dark:text-blue-600"
                  value={companyFilter}
                  onChange={(e) => setCompanyFilter(e.target.value)}
                >
                  <option>ALL</option>
                  <option>AB</option>
                  <option>LTD</option>
                </Select>

                <Select
                  className="bg-transparent dark:bg-transparent p-0 font-semibold text-blue-600 dark:text-blue-600"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <option value="RECENT">Recent 200 Stock Records</option>
                  <option value="ALL">All</option>
                </Select>
              </div>
            </th>
            {products.map((product) => (
              <Fragment key={product.odooId}>
                <th
                  className="px-4 text-right border-r border-l-4 bg-gray-200 dark:bg-gray-700 dark:border-gray-700 pb-0"
                  colSpan={getKeys(product).length}
                >
                  <div className="flex items-center justify-end">
                    <a
                      className="text-purple-600"
                      href={`${odooUrl}/web#id=${product.odooId}&menu_id=222&cids=1&action=563&model=product.product&view_type=form`}
                      target="_blank"
                    >
                      W#{product.id},OD#{product.odooId}:[{product.number}]{" "}
                      {product.name}
                    </a>
                  </div>
                </th>
              </Fragment>
            ))}
          </tr>
          <tr className="text-right sticky bg-gray-200 dark:bg-gray-700 top-[24px] z-10">
            {products.map((product) => (
              <Fragment key={product.odooId}>
                {getKeys(product).map((key) => (
                  <th
                    className={`px-4 pt-0 text-right font-normal z-20
                    ${key === "Forecasted" ? "border-r border-gray-200 dark:border-gray-700" : ""}
                    ${key === "QTY" ? "border-l-4 " : ""}
                    `}
                    key={key}
                  >
                    {key}
                  </th>
                ))}
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {res.map((stock, index) => (
            <tr
              key={index}
              className={`hover:bg-blue-50 hover:dark:bg-blue-900 hover:bg-blend-multiply`}
            >
              <td
                className={`border border-gray-100 dark:border-gray-700 px-4 sticky left-0 bg-white dark:bg-gray-800 z-10
              ${rowBgColor(stock.type)}
              `}
              >
                <div className="flex items-center space-x-2">
                  <div className="w-20">{stock.date}</div>
                  <div className="w-12">
                    {ODOO_COMPANY_NAMES[stock.company_id]}
                  </div>
                  <div className="flex justify-between flex-1">
                    <span className="text-xs opacity-70">
                      [{stock.type}] {stock.origin}
                    </span>

                    <span
                      className={`font-bold pl-4 uppercase
                  ${stock.state === "done" || stock.state === "HANDLED" ? "text-green-600" : "text-orange-600"}
                  `}
                    >
                      {stock.state}
                    </span>
                  </div>
                </div>
              </td>
              {products.map((product) => (
                <Fragment key={product.odooId}>
                  {getKeys(product).map((key) => (
                    <td
                      className={`text-right border border-gray-100 dark:border-gray-700 px-4
                    ${key === "QTY" ? " border-l-4 " : ""}
                    ${cellStyle(stock, product)}
                    `}
                      key={key}
                    >
                      {key === "QTY" &&
                      stock.type === "PO" &&
                      stock[`${product.odooId} ${key}`] === 0
                        ? `(±${
                            stock[`${product.odooId} odoo_qty`] ||
                            stock[`${product.odooId} odoo_ltd_qty`]
                          }) `
                        : null}
                      {stock[`${product.odooId} ${key}`]}
                    </td>
                  ))}
                </Fragment>
              ))}
            </tr>
          ))}
        </tbody>
        {lastRow && (
          <tfoot>
            <tr className=" sticky bottom-0 bg-white dark:bg-gray-700 z-10 font-semibold text-base">
              <td className="px-4 sticky left-0 z-20 bg-white dark:bg-gray-700">
                Summary
              </td>
              {products.map((product) => (
                <Fragment key={product.odooId}>
                  <td></td>

                  {(companyFilter === "ALL" || companyFilter === "AB") && (
                    <td className="text-right px-4">
                      {lastRow[`${product.odooId} ODOO AB`]}
                    </td>
                  )}
                  {(companyFilter === "ALL" || companyFilter === "LTD") && (
                    <td className="text-right px-4">
                      {lastRow[`${product.odooId} ODOO LTD`]}
                    </td>
                  )}

                  {product.useSupplierStock && (
                    <Fragment>
                      <td className="text-right px-4">
                        {lastRow[`${product.odooId} SS`]}
                      </td>
                      <td className="text-right px-4">
                        {lastRow[`${product.odooId} FC`]}
                      </td>
                    </Fragment>
                  )}
                </Fragment>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
});

export default ChinaStocksProductTableView;
